import BaseModel, { type IResourceName } from 'volta/models/base-model';
import { _normalize, collectionCommand, resourceCommand as command } from 'volta/utils/api';

import { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { IUpdatePOValidationRuleCmd } from 'volta/models/types/po-validation-rule';

export const PoValidationRuleResourceName: IResourceName = {
  singular: 'poValidationRule',
  plural: 'poValidationRules'
};

export enum PoValidationRuleCommands {
  CreatePOValidationRule = 'CreatePOValidationRule',
  UpdatePOValidationRule = 'UpdatePOValidationRule',
  DeletePOValidationRule = 'DeletePOValidationRule',
  ReorderPOValidationRules = 'ReorderPOValidationRules'
}

export interface ValidationRuleCommand {
  name?: string;
  ruleOrder?: number;
  warehouseIds?: string[];
  skuIds?: string[];
  supplierIds?: string[];
  workshopIds?: string[];
  skuCf?: Record<string, unknown>;
  supplierCf?: Record<string, unknown>;
  workshopCf?: Record<string, unknown>;
  daysBefore?: number;
  daysAfter?: number;
  upperPriceThreshold?: number;
  lowerPriceThreshold?: number;
  isActive?: boolean;
}

export default class PoValidationRule extends BaseModel {
  static modelName = 'po-validation-rule';

  @attr('string') name!: string;
  @attr('number') ruleOrder!: number;
  @attr('array', { defaultValue: () => [] }) warehouseIds!: string[];
  @attr('array', { defaultValue: () => [] }) skuIds!: string[];
  @attr('array', { defaultValue: () => [] }) supplierIds!: string[];
  @attr('array', { defaultValue: () => [] }) workshopIds!: string[];
  @attr({
    defaultValue: () => ({})
  })
  skuCf!: Record<string, unknown>;

  @attr({
    defaultValue: () => ({})
  })
  supplierCf!: Record<string, unknown>;

  @attr({
    defaultValue: () => ({})
  })
  workshopCf!: Record<string, unknown>;

  @attr('number') daysBefore!: number;
  @attr('number') daysAfter!: number;
  @attr('number') upperPriceThreshold?: number;
  @attr('number') lowerPriceThreshold?: number;
  @attr('boolean', { defaultValue: true }) isActive!: boolean;

  static createPoValidationRule = collectionCommand<
    ValidationRuleCommand,
    PoValidationRule,
    typeof PoValidationRule
  >(PoValidationRuleCommands.CreatePOValidationRule);

  static reorderPoValidationRules = collectionCommand<
    { ids: string[] },
    PoValidationRule[],
    typeof PoValidationRule
  >(PoValidationRuleCommands.ReorderPOValidationRules);

  updatePoValidationRule = command<IUpdatePOValidationRuleCmd, PoValidationRule>(
    PoValidationRuleCommands.UpdatePOValidationRule
  );

  deletePoValidationRule = command<undefined, PoValidationRule>(
    PoValidationRuleCommands.DeletePOValidationRule
  );

  toCmd() {
    return {
      name: this.name,
      ruleOrder: this.ruleOrder,
      warehouseIds: this.warehouseIds,
      skuIds: this.skuIds,
      supplierIds: this.supplierIds,
      workshopIds: this.workshopIds,
      skuCf: this.skuCf,
      supplierCf: this.supplierCf,
      workshopCf: this.workshopCf,
      daysBefore: this.daysBefore,
      daysAfter: this.daysAfter,
      upperPriceThreshold: this.upperPriceThreshold,
      lowerPriceThreshold: this.lowerPriceThreshold,
      isActive: this.isActive
    };
  }

  [Type] = 'po-validation-rule' as const;
}
